// Libraries
import dayjs from 'dayjs';
import { DateTime } from 'luxon';

// Utils
import {
  AWS_S3,
  NO_TEXT,
  YES_TEXT,
  StatusKey,
  FORMAT_DATE,
  STORAGE_KEYS,
  TIME_REFRESH_TOKEN,
} from 'utils/constants';
import { ExportOptions, GetListData } from 'utils/types';

import Encoding from 'encoding-japanese';
export const getCurrentDate = (): DateTime => {
  return DateTime.now();
};

export const parseJwt = (): Record<string, unknown> | undefined => {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  if (!accessToken) {
    return undefined;
  }

  const base64Url = accessToken.split('.')[1];

  if (!base64Url) {
    throw new Error('Invalid token structure');
  }

  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    throw new Error('Invalid token');
  }
};

export const isTokenExpired = (): boolean => {
  const currentTime = getCurrentDate().toSeconds();
  const payload = parseJwt();
  const expToken = payload?.exp as number;
  return expToken !== undefined ? expToken < currentTime + TIME_REFRESH_TOKEN : true;
};

export const constructEndpoint = (
  endpointArray: string[],
  params: (string | number)[]
): string[] => {
  const endpoint = endpointArray[0].replace(/{(\d+)}/g, (_, index: number): string => {
    return params[index] !== undefined ? (params[index] as string) : '';
  });

  const method = endpointArray[1];
  return [endpoint, method];
};

export const handleFileExport = async ({
  apiCall,
  fileName,
  titleError,
  setIsLoading,
  setNotify,
}: ExportOptions): Promise<void> => {
  try {
    setIsLoading(true);
    const res = await apiCall();
    const sjisArray = Encoding.convert(new TextEncoder().encode(res.data), {
      to: 'SJIS',
      from: 'UTF8',
      bom: true,
    });

    const blob = new Blob([new Uint8Array(sjisArray)], {
      type: 'text/csv;charset=SJIS',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}_${DateTime.now().toFormat('yyyyMMdd_HHmmss')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    setNotify({
      open: true,
      title: titleError,
      severity: 'error',
    });
    console.error(error);
  } finally {
    setIsLoading(false);
  }
};

export const handleGetListData = async ({ apiCall, setIsLoading }: GetListData): Promise<any> => {
  try {
    setIsLoading(true);
    const res = await apiCall();
    return res;
  } catch (error) {
    console.error(error);
  } finally {
    setIsLoading(false);
  }
};

export const defaultRadioIsUsed = (status: string): number => {
  return status === StatusKey.IN_USED ? 1 : 0;
};

export const defaultRadioStatusRequested = (status: string): number => {
  return status === StatusKey.REQUESTED || status === StatusKey.CANCELED ? 0 : 1;
};

export const hostName = window.location.hostname;

export const getIconUrl = (icon: string): string => {
  return `${AWS_S3.BASE_URL}/${icon}`;
};

export const formatDate = (date?: Date | null, format = FORMAT_DATE): string => {
  return date ? dayjs(date).format(format) : '';
};

export const renderYesNoText = (hasData: boolean): string => {
  return hasData ? YES_TEXT : NO_TEXT;
};

export const scrollToTop = (): any => {
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
};

export const parseMessage = (message: string, replacements: (string | number)[]): string => {
  let newMessage = message;
  if (newMessage && replacements.length > 0) {
    replacements.forEach((replacement, index) => {
      const argument = '${' + index + '}';
      newMessage = newMessage.replace(argument, `${replacement}`);
    });
  }

  return newMessage;
};

export const formatSquareMeter = (value: string): string => {
  if (!value) {
    return '';
  }
  return formatNumberUs(value.trim()) + '㎡';
};

export const formatCurrencyUnit = (value: string | number): string => {
  if (!value && value !== 0) {
    return '';
  }
  return formatNumberUs(value) + '円';
};

export const formatNumberUs = (input: number | string): string => {
  const number = typeof input === 'string' ? parseFloat(input.trim()) : input;
  if (isNaN(number)) return '';

  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);

  if (formattedNumber.endsWith('.00')) {
    return formattedNumber.split('.00')[0];
  }

  return formattedNumber;
};
